/**
 * Schedule Detail Page - Main component for viewing/editing a schedule
 *
 * Provides tabbed interface for managing all aspects of a schedule:
 * - Overview: Basic schedule info and actions
 * - Classes: Manage dance classes
 * - Rooms: Manage studio rooms
 * - Teachers: Manage instructors
 * - Preferences: Optional scheduling preferences
 * - Results: View scheduling algorithm results
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Typography,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  useGetSchedulesQuery,
  useGetScheduleQuery,
  useGetDisciplinesQuery,
  useGetStylesQuery,
} from '../../api/schedulingApi';
import { useGetUserStudiosQuery } from '../studios/studiosApi';
import { useStudioThemeAccess } from '../../hooks/useStudioThemeAccess';
import Header from '../../components/layout/Header';
import type { ScheduleTab } from '../../types/scheduling';
import ScheduleOverviewTab from './tabs/ScheduleOverviewTab';
import DisciplinesTabDataGrid from './tabs/DisciplinesTabDataGrid';
import StylesTabDataGrid from './tabs/StylesTabDataGrid';
import ClassesTabDataGrid from './tabs/ClassesTabDataGrid';
import RoomsTab from './tabs/RoomsTab';
import TeachersTab from './tabs/TeachersTab';
import PreferencesTab from './tabs/PreferencesTab';
import ResultsTab from './tabs/ResultsTab';
import ScheduleGridTab from './tabs/ScheduleGridTab/ScheduleGridTab';
import PublicPageTab from './tabs/PublicPageTab';
import ImportTab from './tabs/ImportTab/ImportTab';

const ScheduleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState<ScheduleTab>('overview');
  const { eligibleStudios } = useStudioThemeAccess();
  const { data: userStudiosData } = useGetUserStudiosQuery();
  const [studioId, setStudioId] = useState<string | null>(null);

  // Get studio ID from eligible studios (first with Pro access)
  useEffect(() => {
    if (eligibleStudios.length > 0) {
      // Find the first studio, or we could get it from the schedule list
      setStudioId(eligibleStudios[0].id);
    }
  }, [eligibleStudios]);

  // Fetch all schedules to get the studio ID for this specific schedule
  const { data: schedules } = useGetSchedulesQuery(studioId || '', {
    skip: !studioId,
  });

  // Find the studio ID from the schedule in the list
  const scheduleFromList = schedules?.find(s => s.id === id);
  const actualStudioId = scheduleFromList?.studio || studioId;

  const {
    data: schedule,
    isLoading,
    error,
  } = useGetScheduleQuery(
    { id: id || '', studioId: actualStudioId || '' },
    {
      skip: !id || !actualStudioId,
    }
  );

  // Fetch disciplines and styles for record counts
  const { data: disciplines } = useGetDisciplinesQuery(actualStudioId || '', {
    skip: !actualStudioId,
  });

  const { data: styles } = useGetStylesQuery(
    { studioId: actualStudioId || '' },
    { skip: !actualStudioId }
  );

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: ScheduleTab
  ) => {
    setCurrentTab(newValue);
  };

  const handleBack = () => {
    navigate('/scheduling');
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        </Container>
      </>
    );
  }

  if (error || !schedule) {
    return (
      <>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="error">
            Failed to load schedule. Please try again.
          </Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              {schedule.name}
            </Typography>
            {schedule.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {schedule.description}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Tabbed Interface */}
        <Paper sx={{ width: '100%' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minWidth: 100,
              },
            }}
          >
            <Tab label="Overview" value="overview" />
            <Tab label="Schedule Grid" value="grid" />
            <Tab
              label={`Disciplines (${disciplines?.length || 0})`}
              value="disciplines"
            />
            <Tab label={`Styles (${styles?.length || 0})`} value="styles" />
            <Tab label={`Classes (${schedule.class_count})`} value="classes" />
            <Tab label={`Rooms (${schedule.room_count})`} value="rooms" />
            <Tab
              label={`Teachers (${schedule.teacher_count})`}
              value="teachers"
            />
            <Tab label="Preferences" value="preferences" />
            <Tab label="Algorithm Results" value="results" />
            <Tab label="Import" value="import" />
            <Tab label="Public Page" value="public" />
          </Tabs>

          {/* Tab Panels */}
          <Box sx={{ p: 3 }}>
            {currentTab === 'overview' && (
              <ScheduleOverviewTab
                schedule={schedule}
                onTabChange={setCurrentTab}
              />
            )}
            {currentTab === 'grid' && (
              <ScheduleGridTab
                scheduleId={schedule.id}
                studioId={schedule.studio}
              />
            )}
            {currentTab === 'disciplines' && (
              <DisciplinesTabDataGrid studioId={schedule.studio} />
            )}
            {currentTab === 'styles' && (
              <StylesTabDataGrid studioId={schedule.studio} />
            )}
            {currentTab === 'classes' && (
              <ClassesTabDataGrid
                scheduleId={schedule.id}
                studioId={schedule.studio}
              />
            )}
            {currentTab === 'rooms' && (
              <RoomsTab scheduleId={schedule.id} studioId={schedule.studio} />
            )}
            {currentTab === 'teachers' && (
              <TeachersTab
                scheduleId={schedule.id}
                studioId={schedule.studio}
              />
            )}
            {currentTab === 'preferences' && (
              <PreferencesTab
                scheduleId={schedule.id}
                studioId={schedule.studio}
              />
            )}
            {currentTab === 'results' && (
              <ResultsTab scheduleId={schedule.id} studioId={schedule.studio} />
            )}
            {currentTab === 'import' && (
              <ImportTab
                scheduleId={schedule.id}
                studioId={schedule.studio}
                scheduleName={schedule.name}
              />
            )}
            {currentTab === 'public' && (
              <PublicPageTab
                scheduleId={schedule.id}
                studioId={schedule.studio}
                studioSlug={
                  userStudiosData?.studios.find(s => s.id === schedule.studio)
                    ?.slug || 'schedule'
                }
              />
            )}
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default ScheduleDetail;
